import { useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ExternalLink, FileText, Loader2 } from 'lucide-react';
import { useDocsForDevice, useDocsTicketContext } from '@/api/docs';
import { DocumentViewer } from '@/components/docs/DocumentViewer';
import { ExpiryChip } from '@/components/docs/DocsKit';
import { fmtRelative } from '@/lib/cobalt-helpers';
import { docsOrgPath, rmmDevicePath } from '@/lib/routes';
import { SopList, sopHeading, useSopViewer } from '@/components/docs/Sop';

/**
 * IT Glue in context. Both cards render nothing when IT Glue isn't configured or has nothing to
 * say (no linked organisation / no configuration), so they never add noise to a page.
 */

const header = (title: string, right?: React.ReactNode) => (
  <div className="flex items-center justify-between gap-2 px-4 pb-1 pt-3">
    <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</span>
    {right}
  </div>
);

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 border-t border-divider px-4 py-2 first:border-t-0">
      <span className="shrink-0 pt-[2px] text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</span>
      <div className="min-w-0 text-right text-[13px] text-foreground">{children}</div>
    </div>
  );
}

const itgLink = (url: string | null | undefined) =>
  url ? <a href={url} target="_blank" rel="noreferrer" title="Open in IT Glue" className="text-muted-foreground hover:text-foreground"><ExternalLink className="h-3 w-3" /></a> : null;

/**
 * Ticket side rail: the company's IT Glue org (alert + quick notes), the ticket's CI as an IT Glue
 * configuration, the client's On/Offboarding SOP when the title reads like a new starter or leaver,
 * and documents relevant to the ticket title (the section is hidden when none match).
 */
export function ITGlueTicketCard({ companyId, ciId, title }: { companyId: number | null; ciId: number | null; title: string | null }) {
  const q = useDocsTicketContext({ companyId, ciId, title: title ?? undefined });
  const [docId, setDocId] = useState<string | null>(null);
  const sopViewer = useSopViewer();
  const [notesOpen, setNotesOpen] = useState(false);

  if (companyId == null || q.error || (q.data && !q.data.org)) return null;
  if (q.isLoading) {
    return (
      <section className="rounded-lg border border-border bg-card shadow-sm">
        {header('IT Glue')}
        <div className="flex items-center gap-2 px-4 pb-3 pt-1 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading documentation…</div>
      </section>
    );
  }
  const d = q.data!;
  const org = d.org!;
  const notes = org.quickNotes ?? '';

  return (
    <section className="rounded-lg border border-border bg-card shadow-sm">
      {header('IT Glue', <Link to={docsOrgPath(org.id)} className="text-xs text-muted-foreground hover:text-foreground hover:underline">Organisation →</Link>)}
      {org.alert && (
        <div className="mx-4 mb-2 mt-1 flex items-start gap-1.5 rounded-md bg-warning-soft px-2 py-1.5 text-xs text-warning">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" /><span className="whitespace-pre-line">{org.alert}</span>
        </div>
      )}
      {notes && (
        <div className="px-4 pb-2 pt-1">
          <p className={notesOpen ? 'whitespace-pre-line text-xs text-foreground' : 'line-clamp-4 whitespace-pre-line text-xs text-foreground'}>{notes}</p>
          {notes.length > 220 && <button type="button" onClick={() => setNotesOpen((v) => !v)} className="mt-0.5 text-[11px] text-muted-foreground hover:text-foreground hover:underline">{notesOpen ? 'Less' : 'More'}</button>}
        </div>
      )}
      <div className="border-t border-divider">
        {d.configuration && (
          <Row label="Config">
            <span className="inline-flex items-center gap-1.5">
              {d.configuration.rmm ? <Link to={rmmDevicePath(d.configuration.rmm.uid)} className="hover:underline">{d.configuration.name}</Link> : d.configuration.name}
              {itgLink(d.configuration.url)}
            </span>
            <div className="text-[11px] text-muted-foreground">{[d.configuration.type, d.configuration.model].filter(Boolean).join(' · ')}</div>
            {d.configuration.warrantyExpires && <div className="mt-0.5"><ExpiryChip date={d.configuration.warrantyExpires} /></div>}
          </Row>
        )}
      </div>
      {d.sop && (
        <div className="border-t border-divider px-4 py-2">
          <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{sopHeading(d.sop.kind)}</div>
          {d.sop.items.length > 0
            ? <SopList items={d.sop.items} onOpen={sopViewer.setOpen} compact />
            : <p className="text-xs text-muted-foreground">No On/Offboarding SOP in IT Glue for this client. <Link to={docsOrgPath(org.id)} className="hover:text-foreground hover:underline">Organisation →</Link></p>}
        </div>
      )}
      {(d.documents?.length ?? 0) > 0 && (
        <div className="border-t border-divider px-4 py-2">
          <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            Relevant documents
          </div>
          <ul className="space-y-0.5">
            {d.documents!.map((doc) => (
              <li key={doc.id}>
                <button type="button" onClick={() => setDocId(doc.id)} className="flex w-full items-center gap-1.5 rounded px-1 py-0.5 text-left text-xs hover:bg-muted/40" title={doc.updatedAt ? `Updated ${fmtRelative(doc.updatedAt)}` : undefined}>
                  <FileText className="h-3 w-3 shrink-0 text-muted-foreground" />
                  <span className="truncate text-foreground">{doc.name}</span>
                </button>
              </li>
            ))}
          </ul>
          <Link to={docsOrgPath(org.id)} className="mt-1 inline-block text-[11px] text-muted-foreground hover:text-foreground hover:underline">All {d.documentCount} documents →</Link>
        </div>
      )}
      <DocumentViewer id={docId} onClose={() => setDocId(null)} />
      {sopViewer.viewer}
    </section>
  );
}

/** RMM device page: the device's IT Glue configuration (matched by IT Glue's own Datto RMM link). */
export function ITGlueDeviceCard({ uid }: { uid: string }) {
  const q = useDocsForDevice(uid);
  if (q.error) return null;
  if (q.isLoading) {
    return (
      <section className="rounded-lg border border-border bg-card shadow-sm">
        {header('IT Glue')}
        <div className="flex items-center gap-2 px-4 pb-3 pt-1 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Checking IT Glue…</div>
      </section>
    );
  }
  const items = q.data?.items ?? [];
  if (!items.length) {
    return (
      <section className="rounded-lg border border-border bg-card shadow-sm">
        {header('IT Glue')}
        <div className="px-4 pb-3 pt-1 text-xs text-muted-foreground">No IT Glue configuration is linked to this device.</div>
      </section>
    );
  }
  const c = items[0];
  return (
    <section className="rounded-lg border border-border bg-card shadow-sm">
      {header('IT Glue', <Link to={docsOrgPath(c.orgId, 'configurations')} className="text-xs text-muted-foreground hover:text-foreground hover:underline">{c.orgName} →</Link>)}
      <Row label="Configuration"><span className="inline-flex items-center gap-1.5">{c.name}{itgLink(c.url)}</span>{c.archived && <div className="text-[11px] italic text-muted-foreground">archived</div>}</Row>
      <Row label="Type">{c.type ?? '—'}</Row>
      {c.contact && <Row label="Contact">{c.contact}</Row>}
      {c.location && <Row label="Location">{c.location}</Row>}
      <Row label="Warranty"><ExpiryChip date={c.warrantyExpires} /></Row>
      <Row label="Autotask CI">{c.autotaskCiId != null ? `#${c.autotaskCiId}` : <span className="text-muted-foreground">not linked</span>}</Row>
      {items.length > 1 && <div className="border-t border-divider px-4 py-2 text-[11px] text-warning">{items.length} IT Glue configurations point at this device (duplicates).</div>}
    </section>
  );
}
